import { Button } from '@components/atoms/Button/Button';
import { EntityInfo } from '@components/atoms/EntityInfo/EntityInfo';
import { EntityScore } from '@components/atoms/EntityScore/EntityScore';
import { EntitySectionTitle } from '@components/atoms/EntitySectionTitle/EntitySectionTitle';
import { Genre } from '@components/atoms/Genre/Genre';
import { LoadingIndicator } from '@components/atoms/LoadingIndicator/LoadingIndicator';
import { Meta } from '@components/atoms/MetaTags/Meta';
import { StructuredData } from '@components/atoms/MetaTags/StructuredData';
import { Oewa } from '@components/atoms/Oewa/Oewa';
import { Title } from '@components/atoms/Title';
import { FavoriteButton } from '@components/molecules/FavoriteButton/FavoriteButton';
import { Person } from '@components/molecules/Person/Person';
import { TrailerPlayer } from '@components/molecules/TrailerPlayer/TrailerPlayer';
import { WatchLink } from '@components/molecules/Watchlink/Watchlink';
import { ArticleTeaserCard } from '@components/organisms/ArticleTeaserCard/ArticleTeaserCard';
import { CinemaShowtimes } from '@components/organisms/CinemaShowtimes/CinemaShowtimes';
import { DefaultNoSpacing } from '@components/templates/layouts/DefaultNoSpacing';
import {
  GetArticleByIdSimpleDocument,
  GetCinemaDatesDocument,
  GetCinemaStatesDocument,
  GetCinemasDocument,
  GetMovieByIdDocument,
  GetMovieBySlugDocument,
  GetMoviePersonsDocument,
  GetMovieShowtimesUpcomingDocument,
} from '@lib/graphql/generated';
import { extractSsrState } from '@lib/graphql/helpers/extractSsrState';
import { createUrqlClient, usePaginatedQuery, useQuery } from '@lib/graphql/urql';
import { useTagManager } from '@lib/hooks/useTagManager';
import { handleRedirect } from '@lib/redirects/redirect';
import { GetBySlugDocument, type Person as PersonType } from '@src/lib/graphql/generated';
import { useFavoritesStore } from '@src/stores/favorites';
import { decodeOptions } from '@src/utils/urlHelpers';
import { isUuid } from '@utils/common';
import { getCookie } from '@utils/cookies';
import { providers } from '@utils/providers';
import {
  AditionInitialization,
  AditionTagPlacement,
  AditionTagWithFallback,
  Image,
} from '@vgn-medien-holding/vgn-fe-components';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { twMerge } from 'tailwind-merge';
import { useIsClient } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

function getProviderFromSlug(slug) {
  return providers.find((e) => e.slug === slug);
}

interface RouterQuery {
  bundesland?: string;
  kino?: string;
  datum?: string;
}

// Unterseite Filme
const ad_settings_UnterseiteFilme = {
  billboard: {
    id: 5256690,
    name: 'TVM_Unterseite_Filme_Billboard',
    yieldLab: [],
    breakpoints: [3, 4, 5, 6],
  },
  right1: {
    id: 5256695,
    name: 'TVM_Unterseite_Filme_HPA',
    yieldLab: [],
    breakpoints: [3, 4, 5, 6],
  },
  mobile_top: {
    id: 5256611,
    name: 'TVM_Mobile_Unterseite_Filme_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle1: {
    id: 5256613,
    name: 'TVM_Mobile_Unterseite_Filme_MedRec_Cad',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
};

function Movie({ data }) {
  const router = useRouter();
  const routerRef = useRef(router);
  routerRef.current = router;
  const { getTypeFavorites, getFavoriteState } = useFavoritesStore(
    useShallow((state) => ({
      getTypeFavorites: state.getTypeFavorites,
      getFavoriteState: state.getFavoriteState,
      favorites: state.favorites,
    })),
  );

  const isClient = useIsClient();

  const queryParam = router.query as RouterQuery;
  const { movie, oewa } = data;
  const heroImage = movie?.backdrop_image || movie?.cover_image;
  const ogImage = movie?.backdrop_image_og || movie?.cover_image_og;

  const { articles = [] } = data;
  const [coverLoaded, setCoverLoaded] = useState(false);
  const [state, setState] = useState(queryParam.bundesland ?? '');
  const [cinema, setCinema] = useState<string>(queryParam.kino ?? '');
  const [showtimesInView, setShowtimesInView] = useState(false);

  const [cinemaDateStart, setCinemaDateStart] = useState(() => {
    const today = new Date().toJSON().substring(0, 10);
    return data?.dates?.find((item) => item?.date_start?.startsWith(queryParam.datum || today))?.date_start;
  });
  const [cinemaDateEnd, setCinemaDateEnd] = useState(() => {
    const today = new Date().toJSON().substring(0, 10);
    return data?.dates?.find((item) => item?.date_start?.startsWith(queryParam.datum || today))?.date_end;
  });

  const [{ data: cinemasData, fetching: cinemasLoading }] = useQuery({
    query: GetCinemasDocument,
    variables: { state: state },
    pause: !showtimesInView && movie?.showtimesToday?.data?.length < 1,
  });
  const cinemas = cinemasData?.cinemas?.data;

  const [
    {
      fetching: personsLoading,
      stale: personsStale,
      data: personsData,
      hasMorePages: hasMorePersons,
      fetchNextPage: fetchMorePersons,
    },
  ] = usePaginatedQuery({
    query: GetMoviePersonsDocument,
    variables: { id: movie?.id, page: 1, first: 10 },
  });
  const persons = personsData?.flatMap((data) => data?.movie?.persons?.data);

  const [
    {
      data: showtimesUpcomingData,
      fetching: showtimesLoading,
      stale: showtimesStale,
      hasMorePages: hasMoreShowtimes,
      fetchNextPage: fetchMoreShowtimes,
    },
  ] = usePaginatedQuery({
    query: GetMovieShowtimesUpcomingDocument,
    variables: {
      id: movie?.id,
      page: 1,
      first: 7,
      cinema,
      state,
      from: cinemaDateStart,
      until: cinemaDateEnd,
    },
    pause: !showtimesInView && movie?.showtimesToday?.data?.length < 1,
  });
  const showtimesUpcoming = showtimesUpcomingData?.flatMap((data) => data?.movie?.showtimesUpcoming?.data);

  useTagManager({
    event: 'metadata',
    loginStatus: 'nicht eingeloggt',
    seitentyp: 'Film',
    titel: movie.title,
    assetID: oewa?.id,
    aktualisierungsdatum: data?.updated_at,
    erstellungsdatum: data?.created_at,
    oewaSeitenkontingent: oewa?.page_key || 'RedCont',
    oewaSubkontingent: oewa?.category_key || 'KulturUndFreizeit',
    oewaProfilingkontingent: oewa?.profile_key || 'FilmeUndKino',
    cookieAlert: getCookie('OptanonConsent'),
  });

  useEffect(() => {
    if (!cinemasLoading && !cinemas?.find((value) => value?.id === cinema)) {
      setCinema('');
    }
  }, [cinemasLoading, cinemas, cinema]);

  useEffect(() => {
    if (routerRef.current.isReady && showtimesUpcoming?.length > 0) {
      routerRef.current.replace(
        {
          pathname: `/filme/${movie.slug}`,
          query: {
            ...(state && { bundesland: state }),
            ...(cinema && { kino: cinema }),
            ...(cinemaDateStart && { datum: cinemaDateStart.substring(0, 10) }),
          },
        },
        null,
        { shallow: true },
      );
    }
  }, [state, cinema, cinemaDateStart, showtimesUpcoming?.length, movie.slug]);

  const availableProviders = movie?.links
    ?.filter((link) => getProviderFromSlug(link.provider)?.name)
    .sort((l) =>
      isClient
        ? getTypeFavorites('Provider')
            ?.map((e) => e.entity?.id)
            ?.includes(l?.providerEntry?.id)
          ? -1
          : 1
        : 1,
    )
    .sort((l) => (l?.providerEntry?.sponsoring ? -1 : 1));

  return (
    <>
      <Oewa
        pageKey={oewa?.page_key || 'RedCont'}
        categoryKey={oewa?.category_key || 'KulturUndFreizeit'}
        profileKey={oewa?.profile_key || 'FilmUndKino'}
      />
      <AditionInitialization data={{ ad_settings: ad_settings_UnterseiteFilme }} />
      <Head>
        {Meta({ ...data.movie.metadata, open_graph_image: ogImage }, `${data.movie.metadata.meta_title} | TV-MEDIA`)}
      </Head>
      <StructuredData
        image={heroImage?.url}
        entry={data.movie}
        actors={persons.filter((p) => p.meta?.positions.includes('Schauspieler')) as PersonType[]}
      />
      <div className="dark relative h-[120vh] w-full text-white portrait-giant:h-160">
        {heroImage?.url && (
          <Image
            src={heroImage.url}
            alt={movie.title}
            fill
            imageMode="cover"
            copyright={heroImage?.copyright_text}
            sizes="(max-width: 480px) 150vw, 100vw"
            classProps={{ container: 'inset-0 absolute', root: 'object-cover' }}
            priority
            onLoaded={() => {
              setCoverLoaded(true);
            }}
          />
        )}
        <div
          className={twMerge(
            'absolute inset-0 bg-linear-to-b from-black/40 via-black/50 to-black transition-all duration-500',
            !coverLoaded && 'from-black/0 via-black/0',
          )}
        ></div>
        <div className="relative grid h-screen w-full grid-rows-[1fr_auto]">
          <div className="relative grid content-center items-center gap-y-12 px-8">
            <Title
              level={1}
              classProps={{
                heading: 'hyphenate opacity-75 text-center text-4xl md:text-6xl lg:text-7xl lg:leading-smaller',
              }}
            >
              {movie.title}
            </Title>
            <div className="flex flex-wrap justify-center gap-4 text-center">
              {movie.showtimesUpcoming?.data?.length > 0 && (
                <Button title="Jetzt im Kino ansehen" link="#showtimes" buttonStyle="tertiary" />
              )}
              {availableProviders?.length > 0 && (
                <Button
                  title={`Jetzt ${
                    availableProviders.length === 1
                      ? `auf ${getProviderFromSlug(availableProviders[0].provider)?.name}`
                      : ''
                  } streamen`}
                  link={availableProviders.length === 1 ? availableProviders[0].link : '#providers'}
                  buttonStyle="tertiary"
                />
              )}
            </div>
            {movie?.youtube?.length > 0 && (
              <div className="text-center">
                <Button link="#trailer" title="Trailer ansehen" buttonStyle="viewLink" />
              </div>
            )}
          </div>
          <div>
            <aside className="grid w-full items-center divide-y divide-gray-650/25 border-gray-650/25 border-y bg-gray-820/50 px-8 py-6 backdrop-blur-md md:grid-cols-[1fr_auto_1fr] md:gap-6 md:divide-none md:divide-transparent md:px-16">
              <div className="flex min-w-0 justify-center gap-6 py-5 md:justify-start md:py-0">
                {movie?.tvm_score !== 0 ? (
                  <EntityInfo label="Bewertung">
                    <EntityScore max={4} score={movie?.tvm_score - 1} />
                  </EntityInfo>
                ) : movie?.tmdb_score ? (
                  <EntityInfo
                    label="TMDB-Rating"
                    value={movie.tmdb_score?.toLocaleString('de-DE', { maximumFractionDigits: 1 })}
                    unit="%"
                  />
                ) : (
                  <EntityInfo label="TMDB-Rating" value="-" />
                )}
              </div>
              <div className="flex min-w-0 flex-wrap justify-center gap-10 py-5 md:min-w-75">
                <EntityInfo label="Jahr" value={movie?.year} />
                {movie?.age_rating && <EntityInfo label="Alter" value={movie.age_rating} />}
                <EntityInfo label="Spieldauer" value={movie?.runtime} unit="min" />
              </div>
              <div className="contents items-center justify-end gap-4 md:flex">
                <div className="flex min-h-20 flex-wrap items-center justify-center gap-4 border-gray-650/25 border-t py-5 md:min-h-0 md:gap-2 md:border-none md:py-0">
                  {movie?.genres?.map((genre) => (
                    <Genre key={genre.id} genre={genre} />
                  ))}
                </div>
                <div className="flex min-h-16 items-center justify-center gap-4 border-gray-650/25 border-t pt-5 md:min-h-0 md:border-none md:pt-0">
                  <FavoriteButton
                    id={movie?.id}
                    type="Movie"
                    classProps={{ root: 'text-white' }}
                    loginFlowTitle="Speichere jetzt Deine Lieblingsserien und -filme"
                  >
                    <span className="label-sm text-gray-400 md:hidden">
                      {getFavoriteState(movie?.id) ? 'Aus der Watchlist entfernen' : 'Auf die Watchlist geben'}
                    </span>
                  </FavoriteButton>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
      <div className="dark relative z-10 mt-[-20vh] prose-a:text-white text-white portrait-giant:mt-0">
        <main>
          <div className="relative mx-auto grid max-w-screen-xl justify-center gap-4 pt-16 md:grid-cols-[auto_1fr]">
            <article className="ml-auto w-full max-w-screen-md px-8 md:px-16">
              {movie?.summary && (
                <section>
                  <EntitySectionTitle heading>Beschreibung</EntitySectionTitle>
                  <div className="prose prose-streaming" dangerouslySetInnerHTML={{ __html: movie.summary }}></div>
                  <AditionTagPlacement
                    tag={ad_settings_UnterseiteFilme.mobile_top}
                    classProps={{ root: 'mt-8 w-full grid place-items-center md:hidden overflow-hidden' }}
                  />
                </section>
              )}
              {movie?.youtube?.length > 0 && (
                <section id="trailer" className="mt-12 scroll-mt-32 border-gray-650 border-t pt-12">
                  <EntitySectionTitle heading classProps={{ root: 'mt-0' }}>
                    Trailer
                  </EntitySectionTitle>
                  <TrailerPlayer youtubeId={movie.youtube} title={movie.title} />
                </section>
              )}
              {availableProviders?.length > 0 && (
                <section id="providers" className="@container mt-12 scroll-mt-32 border-gray-650 border-t pt-12">
                  <EntitySectionTitle heading classProps={{ root: 'mt-0' }}>
                    Jetzt ansehen
                  </EntitySectionTitle>
                  <div className="grid @xl:grid-cols-2 gap-2">
                    {availableProviders.map((link) => (
                      <WatchLink key={link.providerEntry?.id + link.price + link.pricing} link={link} color="white" />
                    ))}
                  </div>
                  <AditionTagPlacement
                    tag={ad_settings_UnterseiteFilme.mobile_middle1}
                    classProps={{ root: 'mt-8 w-full grid place-items-center md:hidden overflow-hidden' }}
                  />
                </section>
              )}
              {((!!movie?.review && movie?.review !== '<p></p>') || movie?.tvm_score !== 0) && (
                <section className="mt-12 border-gray-650 border-t pt-12">
                  <div>
                    <EntitySectionTitle heading classProps={{ root: 'mt-0' }}>
                      Rezension: Unsere Kritik zum Film
                    </EntitySectionTitle>
                    {movie?.tvm_score !== 0 && <EntityScore max={4} score={movie.tvm_score - 1} />}
                    {movie.review && (
                      <div
                        className="prose prose-streaming pt-2"
                        dangerouslySetInnerHTML={{ __html: movie.review }}
                      ></div>
                    )}
                  </div>
                </section>
              )}
              {articles.length > 0 && (
                <section className="mt-12 min-w-0 border-gray-650 border-t pt-12">
                  <EntitySectionTitle>Verwandte Artikel</EntitySectionTitle>
                  <div className="tvm-scrollbar min-w-0 overflow-x-auto overflow-y-hidden pb-8">
                    <div className="grid h-full grid-flow-col gap-3">
                      {articles.map((article, index) => (
                        <ArticleTeaserCard key={index} article={article} layout="compact" />
                      ))}
                    </div>
                  </div>
                </section>
              )}
            </article>
            {movie?.summary && (
              <div className="relative grid grid-cols-1 justify-end">
                <AditionTagPlacement
                  tag={ad_settings_UnterseiteFilme.right1}
                  classProps={{ root: 'sticky top-28 mb-4 max-w-75 h-fit hidden md:block' }}
                />
              </div>
            )}
          </div>
          <InView
            onChange={(inView) => {
              if (inView === true) {
                setShowtimesInView(true);
              }
            }}
            triggerOnce
          >
            {movie?.showtimesUpcoming?.data?.length > 0 && (
              <article id="showtimes" className="mt-10 w-full scroll-mt-32">
                <Title level={2} classProps={{ heading: 'text-center text-base lg:text-xl' }}>
                  Kinoprogramm
                </Title>
                <div className="mt-6 grid flex-wrap items-center justify-center gap-3 px-4 md:flex">
                  <select
                    className="input-tvm-classic"
                    value={`${cinemaDateStart}|${cinemaDateEnd}`}
                    onChange={(item) => {
                      try {
                        const parts = item?.target.value.split('|');
                        setCinemaDateStart(parts[0]);
                        setCinemaDateEnd(parts[1]);
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                  >
                    {data?.dates?.map(({ date_start, date_end, date_human }) => (
                      <option key={date_start} value={`${date_start}|${date_end}`}>
                        {date_human}
                      </option>
                    ))}
                  </select>
                  <select
                    className="input-tvm-classic"
                    value={state}
                    onChange={(item) => {
                      setState(item?.target.value);
                    }}
                  >
                    <option value="">Bundesland</option>
                    {data.states.map(({ state_code, state_text }) => (
                      <option key={state_code} value={state_code}>
                        {state_text}
                      </option>
                    ))}
                  </select>
                  <select
                    className="input-tvm-classic"
                    value={cinema}
                    onChange={(item) => {
                      setCinema(item?.target.value);
                    }}
                  >
                    <option value="">Kino</option>
                    {cinemas?.map(({ id, name }) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
                {showtimesUpcoming?.length > 0 ? (
                  <div className="mt-6">
                    <CinemaShowtimes showtimes={showtimesUpcoming} noFilter />
                  </div>
                ) : (
                  <div className="mx-auto mt-8 max-w-screen-md px-8 text-center text-xl">
                    Keine Vorstellungen im gewählten Zeitraum.
                  </div>
                )}
                <LoadingIndicator visible={showtimesLoading} />
                {hasMoreShowtimes && (
                  <div className="text-center">
                    <Button
                      title="Mehr anzeigen"
                      hasAction
                      isLoading={showtimesLoading || showtimesStale}
                      onAction={() => setTimeout(fetchMoreShowtimes)}
                      buttonStyle="secondary"
                      classProps={{ root: 'mt-6' }}
                    />
                  </div>
                )}
              </article>
            )}
          </InView>
          {movie?.tvm_score !== null && movie.tvm_score !== undefined && (
            <div className="grid place-items-center">
              <AditionTagWithFallback
                tag={ad_settings_UnterseiteFilme.billboard}
                fallback={ad_settings_UnterseiteFilme.mobile_top}
                breakpoint={'lg'}
                classProps={{ root: 'mt-6' }}
              />
            </div>
          )}
          <article className="mt-16 px-4 sm:px-8 lg:px-16">
            {persons?.length > 0 && (
              <>
                <Title level={2} classProps={{ heading: 'text-base lg:text-xl' }}>
                  Darsteller und Crew
                </Title>
                <LoadingIndicator visible={persons?.length < 1 && personsLoading} />
                <div className="mt-6 grid grid-cols-fill-actors justify-items-stretch gap-4">
                  {persons?.map((person) => (
                    <Person key={person.id} person={person} classProps={{ root: 'w-full' }} />
                  ))}
                </div>
                {hasMorePersons && (
                  <div className="text-center">
                    <Button
                      title="Mehr anzeigen"
                      hasAction
                      isLoading={personsLoading || personsStale}
                      onAction={() => setTimeout(fetchMorePersons)}
                      buttonStyle="secondary"
                      classProps={{ root: 'mt-6' }}
                    />
                  </div>
                )}
              </>
            )}
          </article>
        </main>
      </div>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { ssrQuery, ssrCache } = createUrqlClient();

  const options = decodeOptions(params?.slug);

  const { data: movieData } = isUuid(options.slug)
    ? await ssrQuery({
        query: GetMovieByIdDocument,
        variables: { id: options.slug, includeHasShowtimes: true },
      })
    : await ssrQuery({
        query: GetMovieBySlugDocument,
        variables: { slug: options.slug, includeHasShowtimes: true },
      });
  const movie = movieData?.movie;

  // Handle redirect result
  if (movie?.metadata?.redirect) {
    return handleRedirect(301, movie?.metadata?.redirect);
  }

  // Redirect asset-ID (UUID) URLs to the canonical slug URL to avoid duplicate content
  if (isUuid(options.slug) && movie?.published && movie?.slug && movie.slug !== options.slug) {
    return handleRedirect(301, `/filme/${movie.slug}`);
  }

  if (!movie?.published) {
    return {
      redirect: {
        permanent: false,
        destination: '/404',
      },
    };
  }

  if (!movie) {
    // Check if a redirect is in place
    const { data: pageData } = await ssrQuery({
      query: GetBySlugDocument,
      variables: {
        portal: 'tvmedia',
        slug: `/filme/${options.slug}`,
      },
    });

    console.error('RE', pageData);

    // Handle redirect result
    if (pageData?.findBySlug?.__typename === 'Redirect' && pageData?.findBySlug?.redirect_type) {
      return handleRedirect(
        pageData?.findBySlug?.redirect_type,
        pageData?.findBySlug?.redirect_target != null ? pageData?.findBySlug?.redirect_target : '',
      );
    }
  }

  if (!movie) return { notFound: true };

  await ssrQuery({
    query: GetMoviePersonsDocument,
    variables: { id: movie.id, page: 1, first: 10 },
  });

  const { data: statesData } = await ssrQuery({
    query: GetCinemaStatesDocument,
    variables: {},
  });
  const states = statesData?.getStates;

  const { data: cinemaData } = await ssrQuery({
    query: GetCinemaDatesDocument,
    variables: {},
  });
  const dates = cinemaData?.cinemaDates;

  let articles = [];
  if (movie.article_ids?.length > 0) {
    const articleResults = await Promise.all(
      movie.article_ids.map((id) => ssrQuery({ query: GetArticleByIdSimpleDocument, variables: { id } })),
    );
    articles = articleResults.map((r) => r.data?.article).filter(Boolean);
  }

  const data = {
    movie,
    states,
    dates,
    articles,
  };

  return {
    revalidate: 60 * 60 * 24 * 2, // 2 days
    props: {
      data,
      urqlState: extractSsrState(ssrCache),
    },
  };
};

Movie.PageLayout = DefaultNoSpacing;

export default Movie;
